import { FilterState, initialFilterState, StatFilterGroup } from '../models/filter';
import {
  setRarity,
  setType,
  setItemType,
  setNewItems,
  setEthereal,
  setUnidentified,
  setLimitFilter,
  setViewType,
  setStatFilterGroups,
  clearStatFilters,
  setSearchTerm,
  applyFilterSearch,
} from '../features/filters/filterSlice';
import { Dispatch } from 'redux';
import config from '../config/config.json';
import axios from 'axios';

export class D2RSService {
  static convertFiltersToSearchString(filters: FilterState): string {
    const searchParts: string[] = [];

    // Handle item type and category - these should go first without comma
    let firstPart = '';
    if (filters.itemType !== '' && filters.itemType !== '0') {
      firstPart = filters.itemType;
    } else if (filters.type !== '' && filters.type !== '0') {
      firstPart = filters.type;
    }

    // Handle rarity filter
    if (filters.rarity !== '') {
      searchParts.push(filters.rarity);
    }

    // Handle new items filter
    if (filters.newItems === 'new-items-only') {
      searchParts.push('new');
    } else if (filters.newItems === 'no-new-items') {
      searchParts.push('!new');
    }

    // Handle eth items filter
    if (filters.ethereal === 'eth') {
      searchParts.push('eth');
    } else if (filters.ethereal === 'non-eth') {
      searchParts.push('!eth');
    }

    // Handle unid filter
    if (filters.unidentified === 'unid') {
      searchParts.push('unid');
    } else if (filters.unidentified === 'non-unid') {
      searchParts.push('!unid');
    }

    filters.excludeStatFilters.forEach((stat) => {
      // Always use name and not ID
      const statName = stat.name.toLowerCase();
      searchParts.push(`!${statName}`);
    });

    filters.statFilterGroups.forEach((group) => {
      if (group.filters.length >= 2) {
        const statNames = group.filters.map((filter) => {
          // Always use name and not ID
          const statName = filter.name.toLowerCase();
          const shortStatName = statName.includes('strength')
            ? 'str'
            : statName.includes('dexterity')
              ? 'dex'
              : statName;

          if (filter.min === filter.max && filter.min > 0) {
            return `${shortStatName}=${filter.min}`;
          } else if (filter.min > 0 && filter.max > 0) {
            return `${shortStatName}=${filter.min}-${filter.max}`;
          } else if (filter.min > 0) {
            return `${shortStatName}=${filter.min}`;
          }
          return shortStatName;
        });
        searchParts.push(statNames.join(' or '));
      } else if (group.filters.length === 1) {
        const filter = group.filters[0];
        // Always use name and not ID
        const statName = filter.name.toLowerCase();
        if (filter.min === filter.max && filter.min > 0) {
          searchParts.push(`${statName}=${filter.min}`);
        } else if (filter.min > 0 && filter.max > 0) {
          searchParts.push(`${statName}=${filter.min}-${filter.max}`);
        } else if (filter.min > 0) {
          searchParts.push(`${statName}=${filter.min}`);
        } else {
          searchParts.push(statName);
        }
      }
    });

    // Only add view and limit as query parameters if there's no other search parts
    // Otherwise include in the comma-separated format
    const isSimpleSearch = searchParts.length === 0 && firstPart === '';

    // Handle view type
    if (filters.viewType !== 'all') {
      if (isSimpleSearch) {
        searchParts.push(`view:${filters.viewType}`);
      } else {
        searchParts.push(filters.viewType);
      }
    }

    // Only add limit filter if it's not the default (not 100)
    if (filters.limitFilter !== 100) {
      searchParts.push(`limit=${filters.limitFilter}`);
    }

    // Remove duplicates using filter method
    const uniqueParts = searchParts.filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

    // Build final search string - first part goes without comma, rest with commas
    let result = '';
    if (firstPart) {
      result = firstPart;
      if (uniqueParts.length > 0) {
        result += ',' + uniqueParts.join(',');
      }
    } else {
      // When there's no firstPart (itemType or type), start with comma if there are any parts
      if (uniqueParts.length > 0) {
        result = ',' + uniqueParts.join(',');
      }
    }

    return result;
  }

  static parseSearchString(searchString: string): FilterState {
    const filters = {
      ...initialFilterState,
      excludeStatFilters: [...initialFilterState.excludeStatFilters],
      statFilterGroups: [...initialFilterState.statFilterGroups],
    };

    if (!searchString.trim()) {
      return filters;
    }

    // Split by commas to get individual search terms and filter out empty parts
    const parts = searchString
      .trim()
      .split(',')
      .filter((part) => part.trim() !== '');

    // Create a map to collect filters by group
    const groupsMap: Record<string, StatFilterGroup> = {};
    let orGroupCounter = 0;

    // First part might be the item type if it doesn't contain a colon or special character
    if (
      parts.length > 0 &&
      !parts[0].includes(':') &&
      !parts[0].includes('=') &&
      !parts[0].startsWith('!') &&
      !parts[0].startsWith('.')
    ) {
      filters.itemType = parts[0].trim();
      parts.shift(); // Remove the first element
    }

    parts.forEach((part) => {
      const trimmedPart = part.trim();

      // Skip empty parts
      if (!trimmedPart) {
        return;
      }

      // Handle OR conditions with min-max values (e.g., "str=10 or dex=15")
      if (trimmedPart.includes(' or ')) {
        const orParts = trimmedPart.split(' or ');
        const groupId = `orGroup${orGroupCounter++}`;

        groupsMap[groupId] = {
          id: groupId,
          filters: orParts.map((orPart) => {
            // Extract values if present
            let statId = orPart.trim();
            let min = 0;
            let max = 0;

            // Handle min-max format (e.g., "str=10-15")
            if (statId.includes('=')) {
              const [statName, valueStr] = statId.split('=');
              statId = statName.trim();

              // Check if it's a range (contains hyphen)
              if (valueStr.includes('-')) {
                const [minStr, maxStr] = valueStr.split('-');
                min = parseInt(minStr, 10);
                max = parseInt(maxStr, 10);
              } else {
                // Single value
                min = parseInt(valueStr, 10);
              }
            }

            // Map abbreviated stat names to full names
            let statName = statId;
            if (statId === 'str') statName = 'strength';
            if (statId === 'dex') statName = 'dexterity';

            return {
              id: statName.replace(/\s+/g, '_'),
              name: statName,
              min,
              max,
            };
          }),
        };
        return;
      }

      // Handle exact match (e.g., ".el rune")
      if (trimmedPart.startsWith('.')) {
        filters.itemType = trimmedPart.substring(1).trim();
        return;
      }

      // Handle negation with special cases
      if (trimmedPart.startsWith('!')) {
        const negatedValue = trimmedPart.substring(1).trim();

        // Handle special cases like !eth
        if (negatedValue === 'eth') {
          filters.ethereal = 'non-eth';
          return;
        } else if (negatedValue === 'new') {
          filters.newItems = 'no-new-items';
          return;
        } else if (negatedValue === 'unid') {
          filters.unidentified = 'non-unid';
          return;
        }

        // For excluded stats, handle min/max values if present
        let statName = negatedValue;
        let min = 0;
        let max = 0;

        // Check if it's a value with range (e.g., "!defense=100-120")
        if (negatedValue.includes('=')) {
          const [statPart, valuePart] = negatedValue.split('=');
          statName = statPart.trim();

          // Check if it's a range with hyphen
          if (valuePart.includes('-')) {
            const [minStr, maxStr] = valuePart.split('-');
            min = parseInt(minStr, 10) || 0;
            max = parseInt(maxStr, 10) || 0;
          } else {
            // Single value
            min = parseInt(valuePart, 10) || 0;
            max = min; // Same value for both min and max
          }
        }

        // Add the stat to excludeStatFilters
        filters.excludeStatFilters.push({
          id: statName.replace(/\s+/g, '_'),
          name: statName,
          min,
          max,
        });
        return;
      }

      // Handle key-value pairs with equals sign
      if (trimmedPart.includes('=')) {
        const [key, value] = trimmedPart.split('=');
        const keyTrimmed = key.trim().toLowerCase();

        // Handle ranges (e.g., "defense=100-120")
        if (value.includes('-')) {
          const [minStr, maxStr] = value.split('-');
          const min = parseInt(minStr, 10);
          const max = parseInt(maxStr, 10);

          // Add as stat filter
          const groupId = `singleStat_${keyTrimmed}`;
          groupsMap[groupId] = {
            id: groupId,
            filters: [
              {
                id: keyTrimmed.replace(/\s+/g, '_'),
                name: keyTrimmed,
                min,
                max,
              },
            ],
          };
          return;
        }

        // Handle numeric values (e.g., "defense=141")
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
          // Check for limit as a special case
          if (keyTrimmed === 'limit') {
            filters.limitFilter = numValue;
            return;
          }

          // Add as stat filter
          const groupId = `singleStat_${keyTrimmed}`;
          groupsMap[groupId] = {
            id: groupId,
            filters: [
              {
                id: keyTrimmed.replace(/\s+/g, '_'),
                name: keyTrimmed,
                min: numValue,
                max: numValue.toString().startsWith('0') ? 0 : numValue, // If it starts with 0, it's just a presence check
              },
            ],
          };
          return;
        }

        return;
      }

      // Handle special keywords
      switch (trimmedPart.toLowerCase()) {
        case 'unique':
          filters.rarity = 'unique';
          break;
        case 'set':
          filters.rarity = 'set';
          break;
        case 'rare':
          filters.rarity = 'rare';
          break;
        case 'magic':
          filters.rarity = 'magic';
          break;
        case 'white':
          filters.rarity = 'white';
          break;
        case 'misc':
          filters.rarity = 'misc';
          break;
        case 'gem':
          filters.rarity = 'gem';
          break;
        case 'runeword':
          filters.rarity = 'runeword';
          break;
        case 'eth':
          filters.ethereal = 'eth';
          break;
        case 'unid':
          filters.unidentified = 'unid';
          break;
        case 'new':
          filters.newItems = 'new-items-only';
          break;
        case 'products':
        case 'instant':
          filters.viewType = trimmedPart.toLowerCase() as FilterState['viewType'];
          break;
        default:
          // Check if it's a key:value format
          if (trimmedPart.includes(':')) {
            const [key, value] = trimmedPart.split(':');
            switch (key) {
              case 'rarity':
                filters.rarity = value as FilterState['rarity'];
                break;
              case 'type':
                filters.type = value;
                break;
              case 'itemtype':
                filters.itemType = value;
                break;
              case 'view':
                filters.viewType = value as FilterState['viewType'];
                break;
              case 'limit':
                filters.limitFilter = parseInt(value, 10);
                break;
              default:
                // Don't add to excludeStatFilters for unrecognized key:value pairs
                // This would happen with the stat filter groups processing above
                break;
            }
          } else {
            // For regular non-special keywords, we don't default to adding them as excludeStatFilters
            // They could be part of a stat filter which should be handled by the previous processing logic
            break;
          }
      }
    });

    // Convert the groups map to an array
    filters.statFilterGroups = Object.values(groupsMap);

    return filters;
  }

  static navigateWithFilters(
    filterUpdates: Partial<FilterState>,
    navigate: (route: string, params?: any) => void,
    shopRoute: string = '/shop',
    dispatch?: Dispatch<any>
  ): void {
    // Create a new filter state by merging initial state with updates
    const updatedFilters: FilterState = {
      ...initialFilterState,
      ...filterUpdates,
    };

    // Generate search string from the updated filters
    const searchString = this.convertFiltersToSearchString(updatedFilters);

    // Update Redux state if dispatch is provided
    if (dispatch) {
      // Clear existing filters first
      dispatch(clearStatFilters());
      dispatch(setStatFilterGroups([]));

      // Set individual filter values
      if (updatedFilters.rarity !== '') {
        dispatch(setRarity(updatedFilters.rarity));
      }

      if (updatedFilters.type !== '') {
        dispatch(setType(updatedFilters.type));
      }

      if (updatedFilters.itemType !== '') {
        dispatch(setItemType(updatedFilters.itemType));
      }

      if (updatedFilters.newItems !== '') {
        dispatch(setNewItems(updatedFilters.newItems));
      }

      if (updatedFilters.ethereal !== '') {
        dispatch(setEthereal(updatedFilters.ethereal));
      }

      if (updatedFilters.unidentified !== '') {
        dispatch(setUnidentified(updatedFilters.unidentified));
      }

      if (updatedFilters.limitFilter !== 100) {
        dispatch(setLimitFilter(updatedFilters.limitFilter));
      }

      if (updatedFilters.viewType !== 'all') {
        dispatch(setViewType(updatedFilters.viewType));
      }

      // Set stat filter groups
      dispatch(setStatFilterGroups(updatedFilters.statFilterGroups));

      // Set the search term and trigger search
      if (searchString) {
        dispatch(setSearchTerm(searchString));
        dispatch(applyFilterSearch());
      }
    }

    // Navigate to the shop page with search term as query parameter
    const shopUrl = searchString
      ? `${shopRoute}?term=${encodeURIComponent(searchString)}`
      : shopRoute;
    navigate(shopUrl);
  }

  static async fetchDynamicPickitPackById(id: number) {
    const response = await axios.get(`${config.backend_base_url}/api/dynamic-pickit/packs/${id}`);
    return response.data;
  }
}
